import { useMemo, useState } from "react";
import {
  Copy,
  EditIcon,
  Package,
  Plus,
  Trash2 as Trash,
} from "@/components/icons";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Input,
  Label,
  toast,
} from "@/components/kit";
import { cn } from "@/lib/utils";
import type { ProductVariantDTO } from "@/modules/products/domain/types";
import { formatLak } from "@/shared/lib/currency";
import { useBulkUpdateVariantPrices } from "../api/queries";
import { ProductFormSection } from "./ProductFormSection";
import { VariantDeleteDialog } from "./VariantDeleteDialog";
import { VariantFormDialog } from "./VariantFormDialog";

export type ProductVariantsPanelProps = {
  productId: string;
  variants: ProductVariantDTO[];
  defaultLowStockThreshold: number;
  canManage?: boolean;
  onChanged?: () => void;
  /** Phase 25 — ຫຼັງສ້າງ variant ໃໝ່ (ກັບໄປໃບສັ່ງນຳເຂົ້າ) */
  onVariantCreated?: (variant: ProductVariantDTO) => void;
};

type DialogState =
  | { type: "closed" }
  | {
      type: "form";
      mode: "create" | "edit" | "copy";
      variant?: ProductVariantDTO;
    }
  | { type: "delete"; variant: ProductVariantDTO };

function stockLabel(variant: ProductVariantDTO) {
  if (variant.inventoryVariantId) {
    const saleable =
      variant.saleableStockQuantity ??
      (variant.poolStockQuantity != null && Number(variant.unitsPerSale) > 0
        ? Math.floor(
            variant.poolStockQuantity / Number(variant.unitsPerSale),
          )
        : null);
    if (saleable != null) {
      return `${saleable} ${variant.stockUnitLabel}`;
    }
    return `ດຶງຄລັງ · ${variant.stockUnitLabel}`;
  }
  return `${variant.stockQuantity} ${variant.stockUnitLabel}`;
}

function canBulkEditPrice(variant: ProductVariantDTO) {
  return !variant.isInventoryOnly;
}

export function ProductVariantsPanel({
  productId,
  variants,
  defaultLowStockThreshold,
  canManage = true,
  onChanged,
  onVariantCreated,
}: ProductVariantsPanelProps) {
  const [dialog, setDialog] = useState<DialogState>({ type: "closed" });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkPriceOpen, setBulkPriceOpen] = useState(false);
  const [bulkPrice, setBulkPrice] = useState("");
  const bulkUpdate = useBulkUpdateVariantPrices();

  const priceEditableVariants = useMemo(
    () => variants.filter(canBulkEditPrice),
    [variants],
  );

  const selectedVariants = useMemo(
    () => variants.filter((variant) => selectedIds.has(variant.id)),
    [selectedIds, variants],
  );

  const allPriceEditableSelected =
    priceEditableVariants.length > 0 &&
    priceEditableVariants.every((variant) => selectedIds.has(variant.id));

  function closeDialog() {
    setDialog({ type: "closed" });
  }

  function toggleVariant(variantId: string, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(variantId);
      else next.delete(variantId);
      return next;
    });
  }

  function toggleSelectAll(checked: boolean) {
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(priceEditableVariants.map((variant) => variant.id)));
  }

  function openBulkPriceDialog() {
    if (selectedVariants.length === 0) return;
    setBulkPrice("");
    setBulkPriceOpen(true);
  }

  async function handleBulkPriceUpdate() {
    const price = Number(bulkPrice);
    if (!Number.isFinite(price) || price < 0 || selectedVariants.length === 0) {
      return;
    }

    try {
      const result = await bulkUpdate.mutateAsync({
        updates: selectedVariants.map((variant) => ({
          variantId: variant.id,
          price,
        })),
      });
      toast.success(`ອັບເດດລາຄາ ${result.updatedCount} ຕົວເລືອກແລ້ວ`);
      setBulkPriceOpen(false);
      setSelectedIds(new Set());
      setBulkPrice("");
      onChanged?.();
    } catch (e) {
      toast.error("ອັບເດດລາຄາບໍ່ສຳເລັດ", {
        description: e instanceof Error ? e.message : undefined,
      });
    }
  }

  return (
    <>
      <ProductFormSection
        icon={Package}
        title="ຕົວເລືອກ & ລາຄາ"
        description="ສີ, ຂະໜາດ ຫຼື ລາຍລະອຽດອື່ນ — ຈັດການແຍກຈາກຂໍ້ມູນສິນຄ້າ"
        action={
          canManage ? (
            <div className="flex flex-wrap items-center gap-2">
              {selectedIds.size > 0 ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="rounded-full"
                  onClick={openBulkPriceDialog}
                >
                  <EditIcon data-icon="inline-start" />
                  ແກ້ລາຄາ ({selectedIds.size})
                </Button>
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-full border-secondary/40"
                onClick={() =>
                  setDialog({ type: "form", mode: "create" })
                }
              >
                <Plus data-icon="inline-start" />
                ເພີ່ມຕົວເລືອກ
              </Button>
            </div>
          ) : null
        }
      >
        {variants.length === 0 ? (
          <Empty className="border border-secondary/25 border-dashed py-10">
            <EmptyHeader>
              <EmptyTitle>ຍັງບໍ່ມີຕົວເລືອກ</EmptyTitle>
              <EmptyDescription>
                ເພີ່ມຕົວເລືອກເພື່ອກຳນົດລາຄາ ແລະ ສະຕັອກ
              </EmptyDescription>
            </EmptyHeader>
            {canManage ? (
              <Button
                type="button"
                className="rounded-full"
                onClick={() =>
                  setDialog({ type: "form", mode: "create" })
                }
              >
                <Plus data-icon="inline-start" />
                ເພີ່ມຕົວເລືອກ
              </Button>
            ) : null}
          </Empty>
        ) : (
          <div className="flex flex-col gap-3">
            {canManage && priceEditableVariants.length > 0 ? (
              <div className="flex items-center gap-2 rounded-xl border border-secondary/25 border-dashed px-3 py-2 text-sm">
                <Checkbox
                  id="select-all-variants"
                  checked={allPriceEditableSelected}
                  onCheckedChange={(checked) =>
                    toggleSelectAll(checked === true)
                  }
                />
                <Label htmlFor="select-all-variants" className="font-normal">
                  ເລືອກທັງໝົດ ({priceEditableVariants.length})
                </Label>
              </div>
            ) : null}

            {variants.map((variant, index) => {
              const isPool = variant.isInventoryOnly;
              const isLinked = Boolean(variant.inventoryVariantId);
              const isBundle = variant.variantType === "bundle";
              const selectable = canManage && canBulkEditPrice(variant);
              const selected = selectedIds.has(variant.id);

              return (
                <div
                  key={variant.id}
                  className={cn(
                    "flex flex-col gap-3 rounded-2xl border border-secondary/20 bg-background/50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between",
                    isPool && "border-secondary/40 bg-secondary/4",
                    isLinked && "border-leaf/30 bg-leaf/3",
                    isBundle && "border-amber-600/25 bg-amber-500/5",
                    selected && "border-primary/35 bg-primary/5",
                  )}
                >
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    {selectable ? (
                      <Checkbox
                        checked={selected}
                        onCheckedChange={(checked) =>
                          toggleVariant(variant.id, checked === true)
                        }
                        className="mt-1"
                        aria-label={`ເລືອກ ${variant.name}`}
                      />
                    ) : null}
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant="secondary"
                          className="rounded-full px-2.5 font-mono text-xs tabular-nums"
                        >
                          #{index + 1}
                        </Badge>
                        <span className="truncate font-medium">
                          {variant.name}
                        </span>
                        {isPool ? (
                          <Badge className="rounded-full border-secondary/30 bg-secondary/10 text-secondary">
                            ຄລັງຮ່ວມ
                          </Badge>
                        ) : null}
                        {isLinked ? (
                          <Badge className="rounded-full border-leaf/30 bg-leaf/10 text-leaf">
                            ໃຊ້ຄລັງຮ່ວມ
                          </Badge>
                        ) : null}
                        {isBundle ? (
                          <Badge className="rounded-full border-amber-600/30 bg-amber-500/10 text-amber-800">
                            ເຊັດ
                          </Badge>
                        ) : null}
                        {!variant.isActive ? (
                          <Badge
                            variant="outline"
                            className="rounded-full text-muted-foreground"
                          >
                            ປິດ
                          </Badge>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground text-sm">
                        <span>
                          {isPool
                            ? "ບໍ່ຂາຍໂດຍຕົງ"
                            : formatLak(variant.price)}
                        </span>
                        <span>{stockLabel(variant)}</span>
                        {variant.sku ? (
                          <span className="font-mono text-xs">
                            SKU: {variant.sku}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {canManage ? (
                    <div className="flex shrink-0 flex-wrap items-center gap-1 ps-7 sm:ps-0">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={() =>
                          setDialog({
                            type: "form",
                            mode: "edit",
                            variant,
                          })
                        }
                      >
                        <EditIcon data-icon="inline-start" />
                        ແກ້ໄຂ
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={() =>
                          setDialog({
                            type: "form",
                            mode: "copy",
                            variant,
                          })
                        }
                      >
                        <Copy data-icon="inline-start" />
                        ສຳເນົາ
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() =>
                          setDialog({ type: "delete", variant })
                        }
                      >
                        <Trash data-icon="inline-start" />
                        ລຶບ
                      </Button>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </ProductFormSection>

      <Dialog open={bulkPriceOpen} onOpenChange={setBulkPriceOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              ແກ້ລາຄາຫຼາຍຕົວເລືອກ
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            <p className="text-muted-foreground text-sm">
              ຈະອັບເດດລາຄາ {selectedVariants.length} ຕົວເລືອກເປັນຄ່າເດີຍວນກັນ
            </p>

            <ul className="max-h-40 space-y-1 overflow-y-auto rounded-xl border border-secondary/20 bg-muted/20 px-3 py-2 text-sm">
              {selectedVariants.map((variant) => (
                <li key={variant.id} className="flex justify-between gap-3">
                  <span className="truncate">{variant.name}</span>
                  <span className="shrink-0 text-muted-foreground tabular-nums">
                    {formatLak(variant.price)}
                  </span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="bulk-variant-price">ລາຄາໃໝ່ (LAK)</Label>
              <Input
                id="bulk-variant-price"
                type="number"
                min={0}
                step="1000"
                value={bulkPrice}
                onChange={(e) => setBulkPrice(e.target.value)}
                placeholder="0"
                autoFocus
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => setBulkPriceOpen(false)}
              disabled={bulkUpdate.isPending}
            >
              ຍົກເລີກ
            </Button>
            <Button
              className="rounded-full"
              disabled={
                bulkUpdate.isPending ||
                bulkPrice === "" ||
                Number(bulkPrice) < 0 ||
                !Number.isFinite(Number(bulkPrice))
              }
              isLoading={bulkUpdate.isPending}
              onClick={() => void handleBulkPriceUpdate()}
            >
              ອັບເດດລາຄາ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VariantFormDialog
        open={dialog.type === "form"}
        onOpenChange={(next) => {
          if (!next) closeDialog();
        }}
        productId={productId}
        defaultLowStockThreshold={defaultLowStockThreshold}
        siblingVariants={variants}
        mode={dialog.type === "form" ? dialog.mode : "create"}
        initialVariant={
          dialog.type === "form" ? dialog.variant : undefined
        }
        onSuccess={(result) => {
          onChanged?.();
          if (result?.mode === "create" && result.variant) {
            onVariantCreated?.(result.variant);
          }
        }}
      />

      <VariantDeleteDialog
        open={dialog.type === "delete"}
        onOpenChange={(next) => {
          if (!next) closeDialog();
        }}
        variantId={dialog.type === "delete" ? dialog.variant.id : null}
        variantName={
          dialog.type === "delete" ? dialog.variant.name : ""
        }
        productId={productId}
        onDeleted={onChanged}
      />
    </>
  );
}
